import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, LogOut, Shield, Search, Bell, Mail, UserPlus, Coins, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";
import { useMore, MORE_GROUPS } from "@/components/Layout";
import { LegalLinks } from "@/pages/Legal";

const QUICK = [
  { to: "/nachrichten", label: "Chats", icon: Mail, id: "messages" },
  { to: "/mitteilungen", label: "Mitteilungen", icon: Bell, id: "notifications" },
  { to: "/einladen", label: "Einladen", icon: UserPlus, id: "invite" },
  { to: "/dashboard", label: "Punkte", icon: Coins, id: "points" },
];

const Row = ({ to, icon: Icon, label, sub, id, gold }) => (
  <Link to={to} data-testid={`menu-${id}`}
    className={`menu-row flex items-center gap-3 px-3.5 py-3 ${gold ? "vip-gold-bg vip-sheen" : "hover:bg-rose-50/70 active:bg-rose-50"}`}>
    <span className={`w-10 h-10 shrink-0 rounded-2xl flex items-center justify-center ${gold ? "bg-amber-300/20 text-amber-300 ring-1 ring-amber-300/40" : "bg-rose-50 text-rose-600"}`}><Icon className="w-5 h-5" /></span>
    <span className="flex-1 min-w-0">
      <span className={`block font-bold text-[15px] leading-tight truncate ${gold ? "vip-gold-text" : "text-slate-800"}`}>{label}</span>
      {sub && <span className={`block text-xs mt-0.5 truncate ${gold ? "text-amber-200/80" : "text-slate-500"}`}>{sub}</span>}
    </span>
    <ChevronRight className={`w-4 h-4 shrink-0 ${gold ? "text-amber-300" : "text-slate-300"}`} />
  </Link>
);

const Group = ({ title, items, testId }) => items.length > 0 && (
  <section className="space-y-2" data-testid={testId}>
    <h2 className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-rose-400 px-2">{title}</h2>
    <div className="bg-white rounded-3xl border border-pink-100 shadow-sm overflow-hidden divide-y divide-pink-50">{items.map((m) => <Row key={m.id} {...m} />)}</div>
  </section>
);

function Hero({ user }) {
  return (
    <Link to={`/u/${user.user_id}`} data-testid="menu-profile" className="menu-hero relative block rounded-[28px] p-5 text-white overflow-hidden shadow-xl shadow-rose-300/40 active:scale-[0.99] transition-transform">
      <span className="absolute -right-10 -bottom-12 w-40 h-40 rounded-full bg-white/10" />
      <div className="relative flex items-center gap-3.5">
        <span className="rounded-full ring-4 ring-white/30"><UserAvatar userId={user.user_id} name={user.name} picture={user.picture} size="w-16 h-16" noStory /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-extrabold text-xl leading-tight truncate">{user.name}</p>
          <p className="text-sm text-white/80">Profil ansehen</p>
        </div>
        <ChevronRight className="w-5 h-5 text-white/70" />
      </div>
      <div className="relative mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-3.5 py-1.5 text-sm font-bold" data-testid="menu-points">
        <Coins className="w-4 h-4 text-amber-200" /> {(user.points ?? 0).toLocaleString("de-DE")} Punkte
      </div>
    </Link>
  );
}

export default function MenuPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const more = useMore();
  const [q, setQ] = useState("");
  const doLogout = async () => { await logout(); navigate("/"); };
  const term = q.trim().toLowerCase();
  const hit = (m) => !term || `${m.label} ${m.sub || ""}`.toLowerCase().includes(term);
  const admin = user.role === "admin" ? [{ to: "/admin", icon: Shield, label: "Admin-Bereich", sub: "Alles verwalten", id: "admin" }].filter(hit) : [];
  const groups = MORE_GROUPS.map((g) => ({ g, items: more.filter((m) => m.g === g && hit(m)) }));
  const empty = !admin.length && groups.every((x) => !x.items.length);
  return (
    <div className="max-w-xl mx-auto space-y-6 pb-6 stagger" data-testid="menu-page">
      <Hero user={user} />
      <div className="grid grid-cols-4 gap-2" data-testid="menu-quick">
        {QUICK.map((a) => (
          <Link key={a.id} to={a.to} data-testid={`menu-quick-${a.id}`} className="flex flex-col items-center gap-1.5 rounded-2xl bg-white border border-pink-100 py-3 active:scale-95 hover:-translate-y-0.5 transition-transform shadow-sm">
            <span className="w-10 h-10 rounded-full pink-gradient text-white flex items-center justify-center shadow-md shadow-rose-200"><a.icon className="w-[18px] h-[18px]" /></span>
            <span className="text-[11px] font-bold text-slate-700">{a.label}</span>
          </Link>
        ))}
      </div>
      <label className="relative block">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Im Menü suchen …" data-testid="menu-search"
          className="w-full h-12 pl-11 pr-10 rounded-2xl bg-white border border-pink-100 text-base focus:outline-none focus:ring-2 focus:ring-rose-300" />
        {q && <button onClick={() => setQ("")} aria-label="Suche leeren" data-testid="menu-search-clear" className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full text-slate-400 hover:bg-rose-50"><X className="w-4 h-4" /></button>}
      </label>
      {groups.map(({ g, items }) => <Group key={g} title={g} items={items} testId={`menu-group-${g}`} />)}
      <Group title="Admin" items={admin} testId="menu-group-admin" />
      {empty && <p className="text-center text-sm text-slate-500 py-6" data-testid="menu-search-empty">Nichts gefunden zu „{q}“</p>}
      <button onClick={doLogout} data-testid="menu-logout" className="w-full h-12 rounded-2xl bg-white border border-pink-100 hover:bg-slate-50 font-semibold text-slate-600 flex items-center justify-center gap-2 transition-colors"><LogOut className="w-4 h-4" /> Abmelden</button>
      <LegalLinks className="justify-center text-xs font-semibold text-slate-400 pt-2" />
    </div>
  );
}
